import React, { useEffect, useRef, useState } from "react";
import { Search } from "semantic-ui-react";
import usePlacesAutocomplete, { getDetails, getGeocode } from "use-places-autocomplete";
import { guid, handleAddressValue } from "../../Helpers";
import { GlobalConstants } from "../constants/GlobalConstants";
import "../../styles/ui.scss";
import { FeatureFlags } from "../constants/LocalServerConstants";
import states from "../../assets/states.json";
import zcta from "../../assets/zip-code-tabulation-areas.json";

interface IBCPlaceDetail {
  address?: string;
  city?: string;
  state?: string;
  county?: string;
  country?: string;
  zip?: string;
} 

interface IBCPlacePostalLocality {
  id: string;
  address: string;
  city: string;
}

interface IBCPlaceSuggestionValue {
  placeId: string;
  description: string;
  cityOverride?: string;
  stateOverride?: string;
}

interface IBCPlaceSuggestion {
  key: string;
  title: string;
  description: string;
  value: IBCPlaceSuggestionValue;
} 

interface IPlaceFieldNameOverride {
  addressComponent: string;
  field: string;
}

interface IBCPlaces {
  placeFields?: string[];
  placeFieldsNameOverrides?: IPlaceFieldNameOverride[];
  placeDetailsObject?: any;
  setPlaceDetailsObject?: any;
  placeDetailsNested?: any;
  handleCustomFormChange?: any;
  handleIsDuplicate?: any;
  handleFormChange?: any;
  searchScopeOptions?: any;
  defaultValue?: string;
  defaultValueOverride?: boolean;
  handleCustomValueOverride?: any;
  autofillOverrides?: string[] | undefined;
  placeholder?: string;
  disabled?: boolean;
  placeDetailsArray?: any;
  fieldType?: string;
  field?: string;
  inputKey?: string;
}

const BCPlaces = ({ ...props }: IBCPlaces) => {
  const searchRef = useRef() as any;
  const [isSearchOpen, setIsSearchOpen] = useState(true as boolean);
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isDefaultValueOverride, setIsDefaultValueOverride] = useState(true as boolean);
  const placeFields = ["address", "city", "state", "county", "country", "zip"];
  const [isFirstInput, setIsFirstInput] = useState(true as boolean);
  const [isSelected, setIsSelected] = useState(false as boolean);
  const isZipInput = props.fieldType && props.fieldType === GlobalConstants.InputTypes.ZIP;
  const [postcodeLocalities, setPostcodeLocalities] = useState([] as IBCPlacePostalLocality[]);
  
  const {
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: props.searchScopeOptions ? props.searchScopeOptions : {
      componentRestrictions: { country: ["us"] }
    },
    debounce: 300,
  });

  const handleInput = (e) => {
    let value = e.target.value;
    if (isZipInput) {
      const replacedValue = value.replace(/[^0-9]+/g, "");
      if (value.length <= 5) {
        value = replacedValue;
      }
      else if (!isNaN(value) && !value.includes("e")) {
        value = value.substring(0, value.length - 1);
      }
    }
    
    setValue(value);
    setIsSearchOpen(true);
    setIsSelected(false);
    setIsFirstInput(false);
    if (value === "" && props.placeDetailsObject && props.setPlaceDetailsObject) {
      const validPlaceFields = props.placeFields ? props.placeFields : placeFields;
      validPlaceFields.forEach(validPlaceField => {
        props.placeDetailsObject[validPlaceField] = undefined;
      });

      props.setPlaceDetailsObject(props.placeDetailsObject);
    }

    const postcode_localities = [] as any[];
    if (isZipInput && value.length === 5) {
      getGeocode({ address: value, componentRestrictions: { country: "US" }}).then((results) => {
        if (results && results[0] && results[0].postcode_localities && results[0].postcode_localities.length > 1) {
          results[0].postcode_localities.forEach(postcode_locality => {
            postcode_localities.push({
              id: guid(),
              address: results[0].formatted_address,
              city: postcode_locality
            });
          });
        }
      }).catch((err) => {
        FeatureFlags.IS_DEBUG && console.log(err);
      });
    }

    setPostcodeLocalities(postcode_localities);
  };

  const handleSelect = async (suggestion) => {
    const result = suggestion.value;
    const placeDetails = await handlePlaceDetails(result.placeId) as IBCPlaceDetail;
    if (result.cityOverride) {
      placeDetails.city = result.cityOverride;
    }

    if (result.stateOverride) {
      placeDetails.state = result.stateOverride;
    }

    if (props.autofillOverrides) {
      const addressObject = {} as IBCPlaceDetail;
      props.autofillOverrides.forEach(autofillOverride => {
        addressObject[autofillOverride] = placeDetails[autofillOverride];
      });

      setValue(handleAddressValue(addressObject));
    }
    else {
      setValue(result.description, false);
    }

    if (props.placeDetailsObject && props.setPlaceDetailsObject && placeDetails && Object.keys(placeDetails).length > 0) {
      Object.entries(placeDetails).forEach(entry => {
        const [key, value] = entry;
        const validPlaceFields = props.placeFields ? props.placeFields : placeFields;
        if (validPlaceFields.includes(key)) {
          let fieldName = key;
          if (props.placeFieldsNameOverrides && Array.from(props.placeFieldsNameOverrides, x => x.addressComponent).includes(key)) {
            const placeFieldsNameOverride = props.placeFieldsNameOverrides.find(x => x.addressComponent === key);
            fieldName = placeFieldsNameOverride && placeFieldsNameOverride.field ? placeFieldsNameOverride.field : key;
          }

          if (props.placeDetailsNested) {
            props.placeDetailsObject[props.placeDetailsNested.array][props.placeDetailsNested.index][fieldName] = value;
          }
          else if (props.placeDetailsArray) {
            props.placeDetailsObject[props.placeDetailsArray.index][fieldName] = value;
          }
          else {
            props.placeDetailsObject[fieldName] = value;
            if (props.handleFormChange) {
              props.handleFormChange(value, fieldName);
            }
          }
        }
      });

      props.setPlaceDetailsObject(props.placeDetailsObject);
    }

    if (props.handleCustomFormChange) {
      if (props.handleIsDuplicate()) {
        setValue("", false);
      }

      props.handleCustomFormChange();
    }

    setIsSelected(true);
    clearSuggestions();
  };

  const renderSuggestions = () => {
    const results = [] as IBCPlaceSuggestion[];
    data.forEach(suggestion => {
      const { place_id, structured_formatting: { main_text, secondary_text } } = suggestion;
      const splitFormattedAddress = secondary_text.split(", ");
      results.push({
        key: place_id,
        title: main_text,
        description: secondary_text,
        value: {
          placeId: place_id,
          description: suggestion.description,
          cityOverride: splitFormattedAddress[0]
        }
      });

      if (isZipInput && value.length === 5) {
        const foundZcta = zcta.find(x => x.zip === value);
        const splitFormattedAddress = secondary_text.split(", ");
        if (foundZcta) {
          foundZcta["city-state"].forEach(foundZctaState => {
            const stateAbbreviation = states.find(x => x.name === foundZctaState.state)?.abbreviation;
            if (splitFormattedAddress[0] !== foundZctaState.city || splitFormattedAddress[1] !== stateAbbreviation) {
              results.push({
                key: guid(),
                title: main_text,
                description: `${foundZctaState.city}, ${stateAbbreviation}, ${splitFormattedAddress[2]}`,
                value: {
                  placeId: place_id,
                  description: suggestion.description,
                  cityOverride: foundZctaState.city,
                  stateOverride: foundZctaState.state
                }
              });
            }
          });
        }
        else if (postcodeLocalities.length > 1) {
          postcodeLocalities.forEach(postcode_locality => {
            if (splitFormattedAddress[0] !== postcode_locality.city) {
              results.push({
                key: guid(),
                title: main_text,
                description: `${postcode_locality.city}, ${splitFormattedAddress[1]}`,
                value: {
                  placeId: place_id,
                  description: suggestion.description,
                  cityOverride: postcode_locality.city
                }
              });
            }
          });
        }
      }
    });

    return results;
  };

  const handlePlaceDetails = async (placeId) => {
    const parameter = {
      placeId: placeId,
      fields: ["address_component"],
    };

    let placeDetails = undefined as any;
    await getDetails(parameter)
      .then((details) => {
        placeDetails = details;
      })
      .catch((error) => {
        console.log("Error: ", error);
      });

    const placeDetail = {
      address: undefined,
      city: undefined,
      state: undefined,
      county: undefined,
      country: undefined,
      zip: undefined
    } as IBCPlaceDetail;
    let streetNumber = "" as string;
    let route = "" as string;
    if (placeDetails && placeDetails.address_components) {
      placeDetails.address_components.forEach(addressComponent => {
        if (addressComponent.types && addressComponent.types.length > 0) {
          if (addressComponent.types.includes("street_number")) {
            streetNumber = addressComponent.long_name;
          }
          else if (addressComponent.types.includes("route")) {
            route = addressComponent.long_name;
          }
          else if ((addressComponent.types.includes("locality") || addressComponent.types.includes("sublocality")) && addressComponent.types.includes("political")) {
            placeDetail.city = addressComponent.long_name;
          }
          else if (addressComponent.types.includes("country")) {
            placeDetail.country = addressComponent.long_name;
          }
          else if (addressComponent.types.includes("administrative_area_level_2")) {
            placeDetail.county = addressComponent.long_name;
          }
          else if (addressComponent.types.includes("administrative_area_level_1")) {
            placeDetail.state = addressComponent.long_name;
          }
          else if (addressComponent.types.includes("postal_code")) {
            placeDetail.zip = addressComponent.long_name;
          }
        }
      });

      if (streetNumber !== "" || route !== "") {
        placeDetail.address = `${streetNumber !== "" ? `${streetNumber} ` : ""}${route}`;
      }

      return placeDetail;
    }
  };

  useEffect(() => {
    const handleFormChangeOverride = (value, field) => {
      if (props.handleFormChange) {
        props.handleFormChange(value, field);
      }
    };

    const handlePlaceDetailAsync = async (placeId) => {
      const placeDetails = await handlePlaceDetails(placeId) as IBCPlaceDetail;
      if (placeDetails.country) {
        setIsDefaultValueOverride(false);
        const validPlaceFields = props.placeFields ? props.placeFields : placeFields;
        validPlaceFields.forEach(validPlaceField => {
          let fieldName = validPlaceField;
          if (props.placeFieldsNameOverrides && Array.from(props.placeFieldsNameOverrides, x => x.addressComponent).includes(validPlaceField)) {
            const placeFieldsNameOverride = props.placeFieldsNameOverrides.find(x => x.addressComponent === validPlaceField);
            fieldName = placeFieldsNameOverride && placeFieldsNameOverride.field ? placeFieldsNameOverride.field : validPlaceField;
          }

          if (props.placeDetailsNested) {
            props.placeDetailsObject[props.placeDetailsNested.array][props.placeDetailsNested.index][fieldName] = placeDetails[validPlaceField];
          }
          else {
            props.placeDetailsObject[fieldName] = placeDetails[validPlaceField];
            handleFormChangeOverride(placeDetails[validPlaceField], fieldName);
          }
        });

        if (props.handleCustomValueOverride) {
          props.handleCustomValueOverride(placeDetails);
        }

        props.setPlaceDetailsObject(props.placeDetailsObject);
        if (props.autofillOverrides) {
          const addressObject = {} as IBCPlaceDetail;
          props.autofillOverrides.forEach(autofillOverride => {
            addressObject[autofillOverride] = placeDetails[autofillOverride];
          });
    
          setValue(handleAddressValue(addressObject));
        }
        else {
          setValue(handleAddressValue({
            city: placeDetails.city,
            state: placeDetails.state,
            zip: placeDetails.zip
          }));
        }
      }
    };

    if (props.defaultValue) {
      if (props.defaultValueOverride && isDefaultValueOverride && data && data[0] && data[0].place_id) {
        handlePlaceDetailAsync(data[0].place_id);
      }
    }
  }, [props.defaultValue, setValue, data, value, placeFields, props.handleFormChange, isDefaultValueOverride, isFirstLoad]);

  useEffect(() => {
    if (props.defaultValue) {
      setValue(props.defaultValue);
    }
  }, [props.defaultValue]);

  useEffect(() => {
    const handleOutsideSearchClick = (e) => {
      if (searchRef.current && searchRef.current.contains(e.target)) {
        setIsSearchOpen(true);
        return;
      }
   
      setIsSearchOpen(false);
    };

    setIsFirstLoad(false);
    document.addEventListener("mousedown", handleOutsideSearchClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideSearchClick);
    };
  }, [searchRef]);

  return (
    <div ref={searchRef}>
      <Search
        className="bc-search-input"
        id={`search-${props.inputKey}-id`}
        value={value}
        open={value !== "" && !isSelected && !isFirstInput && status === "OK" && isSearchOpen}
        results={renderSuggestions()}
        onSearchChange={(e) => handleInput(e)}
        disabled={props.disabled}
        onResultSelect={(e, data) => handleSelect(data.result)}
        placeholder={props.placeholder ? `e.g. ${props.placeholder}` : ""}
        showNoResults
      />
    </div>
  );
};

export default BCPlaces;
